import { FC, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
  useMediaQuery,
  Link,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core";
import ProjectPoolHeading from "../Projects/ProjectPoolHeading";
import cross from "../../assets/images/cross.svg";
import useStyles from "../Styles/ProjectModalStyles";
import { ClaimTokenModalProps } from "../../interfaces";
import WarningIcon from "../../assets/images/WarningIcon.svg";
import ExpireLink from "../../assets/images/ExpireLinkIcon.svg";
import WalletContext from "../../store/Wallet/WalletContext";
import GET_USER_CLAIM_INVESTMENT_DETAILS from "../../utils/queries/subscriptions/getUserClaimInvestmentDetails";
import graphQLClient from "../../utils/authHeader";
import AlertContext from "../../store/Alert/AlertContext";
import {
  getSymbol,
  ChainName,
  NetworkId,
} from "../../store/Wallet/WalletFunction";
import { useAccount, useConfig } from "wagmi";
import { switchChain } from "@wagmi/core";
import CREATE_CLAIM_INTENT from "../../utils/mutations/claim/createClaimIntent";
import { CallExecutionError, TransactionExecutionError } from "viem";
import { LowGasError } from "../../services/web3";
import { useWalletConnect } from "../../services/web3/hooks/useWeb3";
import { Alert, AlertTitle } from "@material-ui/lab";

const ClaimTokenModal: FC<ClaimTokenModalProps> = ({
  open,
  setOpen,
  poolId,
  canRefund,
  modalActionProps,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isActive = useMediaQuery(theme.breakpoints.down("xs"));
  const config = useConfig();

  const { setAlert } = useContext<any>(AlertContext);
  const { fetchTokenClaimed } = useContext<any>(WalletContext);

  const { openConnectModal } = useWalletConnect();
  const [waiting, toggleWait] = useState(true);
  const [userClaimDetails, setUserClaimDetails] = useState<any>({});
  const [tokenSymbol, setSymbol] = useState('');
  const [totalClaimed, setTotalClaimed] = useState(-1);
  const { address: account, chain } = useAccount();
  const [hasClaimed, setHasClaimed] = useState(true);

  // Fetch user claim data
  const variables = {
    poolId: String(poolId),
  };

  useEffect(() => {
    graphQLClient
      .request(GET_USER_CLAIM_INVESTMENT_DETAILS, variables)
      .then((data: any) => {
        setUserClaimDetails(data.getUserClaimInvestmentDetails);
      })
      .catch((error: any) => {
        setAlert("Oops, we were unable to fetch claim details, please try again later", "error");
        setOpen(false);
      });
  }, []);

  useEffect(() => {
    if (
      userClaimDetails &&
      userClaimDetails.distributionScAddress &&
      userClaimDetails.distributionScAddress[0].token
    ) {
      getSymbol(
        userClaimDetails.distributionScAddress[0].networkId,
        userClaimDetails.distributionScAddress[0].token
      )
        .then((symbol: any) => {
          setSymbol(symbol);
        })
        .catch((error: any) => {
          console.log(error);
        });
      fetchTokenClaimed(
        userClaimDetails.distributionScAddress[0].networkId,
        userClaimDetails.distributionScAddress[0].address,
        userClaimDetails.receivingAddress
      )
        .then((response: any) => {
          setTotalClaimed(response);
          toggleWait(false);
        })
        .catch((error: any) => {
          console.error(error);
        });
    }
  }, [userClaimDetails]);

  useEffect(() => {
    if (!userClaimDetails || totalClaimed < 0) return;
    if (totalClaimed < userClaimDetails.totalTokenClaimable)
      setHasClaimed(false);
  }, [totalClaimed, userClaimDetails]);

  const { chainName, claimToken } = useContext<any>(WalletContext);

  const Expire = () => {
    return <img src={ExpireLink} className='img-fluid ' alt='Gains' />;
  };

  const Warning = () => {
    return (
      <img src={WarningIcon} className='img-fluid warningimg' alt='warning' />
    );
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClaim = async () => {

    if (!account && openConnectModal) {
      openConnectModal();
      return
    } 

    toggleWait(true);

    // Switch network if not on the correct network
    if (chain?.id !== userClaimDetails.distributionScAddress[0].networkId) {
      switchChain(config, {
        chainId: userClaimDetails.distributionScAddress[0].networkId,
      })
      .then(() => {
        console.info("Successfully swiched network");
      })
      .catch((error) => {
        setAlert('Unable to switch network, please switch network manually on the wallet to continue', 'error');
      })
    }

    // Create investment intent and call the claim token function
    const network = NetworkId(
      userClaimDetails.distributionScAddress[0].networkId
    );
    const create_claim_intent_variables = {
      poolId: poolId,
      networkId: network,
      walletAddress: userClaimDetails.receivingAddress,
    };

    // TODO: Move to a separate function
    // Follow then and catch patter
    const claimIntent = await graphQLClient.request(
      CREATE_CLAIM_INTENT,
      create_claim_intent_variables
    );

    if (claimIntent) {
      claimToken(
        userClaimDetails.distributionScAddress[0].networkId,
        userClaimDetails.merkleProof.proof,
        userClaimDetails.totalTokenClaimable,
        userClaimDetails.distributionScAddress[0].token,
        userClaimDetails.distributionScAddress[0].address,
        userClaimDetails.receivingAddress
      )
        .then((transactionHash: any) => {
          toggleWait(false);
          setOpen(false);
          modalActionProps?.openClaimActionModal?.({
            status: "success",
            message:
              `You have successfully claimed your ${userClaimDetails.totalTokenClaimable} project tokens.`,
          });
        })
        .catch((error: any) => {
          toggleWait(false);
          setOpen(false);

          if (error instanceof TransactionExecutionError) {
            // if a user rejects
            // do nothing
          } else if (error instanceof LowGasError) {
            modalActionProps?.openClaimActionModal?.({
              status: "error",
              message: error.message,
            });
          } else if (error instanceof CallExecutionError) {
            modalActionProps?.openClaimActionModal?.({
              status: "error",
              message:
                "An error occured in executing your actions, please try again or contact support if issue persist",
            });
          } else {
            modalActionProps?.openClaimActionModal?.({
              status: "error",
              message:
                "An unknown error occurred, please try again or contact support if issue persist",
            });
          }
        });
    } else {
      toggleWait(false);
      setAlert("Unable to create claim intent, please try again.", "error");
    }
  };

  return (
    <>
      <Box component='section'>
        <Dialog
          open={open}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
          onClose={handleClose}
          className={` ${classes.modalpages} ${classes.claimModal}`}
        >
          <Box
            ml={isActive ? "20px" : 7}
            my={isActive ? "20px" : 7}
            mr={isActive ? "20px" : 3}
            className='relative-box'
          >
            {!isActive && (
              <img
                src={cross}
                alt=''
                className='cross-icon'
                onClick={handleClose}
              />
            )}
            <ProjectPoolHeading title1='Claim' title2='Token' />

            <Box mt={2} mr={2}>
              <Typography
                variant='body1'
                className='text-darkslate warning-text '
              >
                Below is your contribution wallet, you can only claim your token
                using this one
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography variant='body1' color='secondary' className='fw-bold'>
                Contribution Address
              </Typography>
              <Typography
                variant='body1'
                color='secondary'
                className='mt-8 text-darkslate'
              >
                {" "}
                {
                  userClaimDetails?.receivingAddress
                  ?
                    isActive
                      ? userClaimDetails?.receivingAddress?.length > 5
                        ? userClaimDetails?.receivingAddress.substr(0, 6) +
                          "..." +
                          userClaimDetails?.receivingAddress.substr(
                            userClaimDetails?.receivingAddress?.length - 4,
                            userClaimDetails?.receivingAddress?.length
                          )
                        : userClaimDetails?.receivingAddress
                      : userClaimDetails?.receivingAddress
                  : "Loading..."
                }
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography variant='body1' color='secondary' className='fw-bold'>
                Network
              </Typography>
              <Typography
                variant='body1'
                color='secondary'
                className='mt-8 text-darkslate'
              >
                {
                  userClaimDetails?.distributionScAddress
                  ?
                    userClaimDetails?.distributionScAddress && userClaimDetails?.distributionScAddress[0]?.networkId && ChainName(userClaimDetails?.distributionScAddress[0]?.networkId)
                  :
                    'Loading...'
                }
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant='body1' color='secondary' className='fw-bold'>
                Token
              </Typography>
              <Typography
                variant='body1'
                color='secondary'
                className='mt-8 text-darkslate'
              >
                ${tokenSymbol}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant='body1' color='secondary' className='fw-bold'>
                Total Tokens Earned
              </Typography>
              <Typography
                variant='body1'
                color='secondary'
                className='mt-8 text-darkslate'
              >
                {userClaimDetails.totalTokenEarned ?? "Loading..."}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant='body1' color='secondary' className='fw-bold'>
                Amount Claimable
              </Typography>
              <Typography
                variant='body1'
                color='secondary'
                className='mt-8 text-darkslate'
              >
                {userClaimDetails.totalTokenClaimable ?? "Loading..."}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant='body1' color='secondary' className='fw-bold'>
                Total Claimed
              </Typography>
              <Typography
                variant='body1'
                color='secondary'
                className='mt-8 text-darkslate'
              >
                {totalClaimed < 0 ? "Loading..." : totalClaimed}
              </Typography>
            </Box>
            {
              canRefund && (
                <Box mt={2}>
                  <Alert severity="warning">
                    Refund request only available if <strong>token not already claimed</strong>.
                  </Alert>
                </Box>
              )
            }

            <Box mt={2} display='flex' alignItems={isActive ? "" : "center"}>
              <Box display="flex" alignItems="center">
                <Expire />
              </Box>
              <Box>
                <Typography
                  variant='subtitle1'
                  className='fw-normal ml-12 font-family-Poppin'
                >
                  By clicking “Claim Tokens” you agree to{" "}
                  <Link
                    href='https://www.gains-associates.com/#/terms-conditions'
                    target='_blank'
                    rel='noopener'
                    className='text-decoration-none'
                  >
                    <Typography
                      variant='subtitle1'
                      color='primary'
                      className=' fw-bold'
                      component='span'
                    >
                      Terms and Conditions
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </Box>

            <Box display='flex' mt={isActive ? 3 : 5}>
              <Box mr={isActive ? 0 : 1} flexGrow={isActive ? 1 : 0}>
                <Button
                  variant='contained'
                  color='secondary'
                  className={`contribute-btn ${waiting ? "action" : ""}`}
                  onClick={() => handleClaim()}
                  disabled={
                    !userClaimDetails ||
                    account && account.toLowerCase() !==
                      userClaimDetails.receivingAddress ||
                    totalClaimed < 0 ||
                    hasClaimed
                  }
                >
                  {waiting && (
                    <CircularProgress
                      className='m-class mr-8'
                      variant='indeterminate'
                      size={15}
                      thickness={2}
                      color='secondary'
                    />
                  )}{" "}
                  {!account
                    ? "Connect Wallet"
                    : !userClaimDetails || totalClaimed < 0
                    ? "Loading..."
                    : account.toLowerCase() !==
                      userClaimDetails.receivingAddress
                    ? "Incorrect Wallet"
                    : chain?.id !==
                      userClaimDetails.distributionScAddress[0].networkId
                    ? "Switch Network"
                    : hasClaimed
                    ? "Tokens Claimed"
                    : "Claim Tokens"}
                </Button>
              </Box>
              <Box ml={isActive ? 0 : 1} flexGrow={isActive ? 1 : 0}>
                <Button
                  variant='contained'
                  color='secondary'
                  className='contribute-btn'
                  disabled={waiting}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </>
  );
};

export default ClaimTokenModal;
